import React, { useEffect, useState } from "react";
import { Zap, MessageCircle, Calendar, RefreshCcw, AlertTriangle, Dumbbell, PlayCircle, Users, Mail, Copy, RotateCw, Trash2, Webhook } from "lucide-react";
import { endpoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const toggles = [
  { key: "auto_message_new_lead", label: "Auto-message new leads", desc: "Send a welcome WhatsApp template the moment a lead is added.", icon: MessageCircle },
  { key: "trial_reminder", label: "Trial reminders", desc: "Queue a reminder one day before a trial.", icon: Calendar },
  { key: "renewal_reminders", label: "Renewal reminders", desc: "Nudge members 7 days, 2 days, and same-day before expiry.", icon: RefreshCcw },
  { key: "inactivity_nudges", label: "Inactivity nudges", desc: "Flag members who haven't visited recently.", icon: AlertTriangle },
];

export default function Automations() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cronRunning, setCronRunning] = useState(false);
  const [cronInfo, setCronInfo] = useState(null);
  const [gym, setGym] = useState(null);
  const [team, setTeam] = useState({ members: [], pending_invitations: [] });
  const [inviteEmail, setInviteEmail] = useState("");
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    Promise.all([
      endpoints.getSettings(),
      endpoints.getGym().catch(() => ({ data: null })),
      endpoints.getTeam().catch(() => ({ data: { members: [], pending_invitations: [] } })),
    ]).then(([s, g, t]) => {
      setSettings(s.data);
      setGym(g.data);
      setTeam(t.data);
    }).finally(() => setLoading(false));
  }, []);

  const save = async (next) => {
    setSaving(true);
    setSettings(next);
    try {
      await endpoints.updateSettings(next);
      toast.success("Saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const runCron = async () => {
    setCronRunning(true);
    try {
      const { data } = await endpoints.runCron();
      setCronInfo(data);
      toast.success(data.created > 0 ? `${data.created} new reminder${data.created === 1 ? "" : "s"} queued` : "No new reminders due");
    } catch {
      toast.error("Cron failed");
    } finally {
      setCronRunning(false);
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    try {
      await endpoints.invite({ email, role: "manager" });
      toast.success(`Invitation sent to ${email}`);
      setInviteEmail("");
      const { data } = await endpoints.getTeam();
      setTeam(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not invite");
    }
  };

  const revokeInvite = async (id) => {
    await endpoints.revokeInvite(id);
    toast.success("Invitation revoked");
    const { data } = await endpoints.getTeam();
    setTeam(data);
  };

  const removeMember = async (user_id, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    try {
      await endpoints.removeTeamMember(user_id);
      toast.success("Team member removed");
      const { data } = await endpoints.getTeam();
      setTeam(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const copyWebhook = async () => {
    if (!gym?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(gym.webhook_url);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Copy failed - select and copy manually");
    }
  };

  const rotateWebhook = async () => {
    if (!window.confirm("Rotate webhook? The old URL will stop working.")) return;
    setRotating(true);
    try {
      await endpoints.rotateWebhook();
      const { data } = await endpoints.getGym();
      setGym(data);
      toast.success("Webhook URL rotated");
    } finally {
      setRotating(false);
    }
  };

  if (loading || !settings) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-3xl mx-auto">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground/70 mb-2">Settings</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground" data-testid="automations-title">
          Automations
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Four simple switches. No complexity.</p>
      </header>

      <div className="border border-border rounded-xl bg-card divide-y divide-border/60 mb-6">
        {toggles.map(({ key, label, desc, icon: Icon }) => (
          <div key={key} className="flex items-start gap-4 p-5">
            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4 text-foreground/85" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground">{label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
            </div>
            <Switch
              data-testid={`toggle-${key}`}
              checked={!!settings[key]}
              onCheckedChange={(v) => save({ ...settings, [key]: v })}
              disabled={saving}
            />
          </div>
        ))}
      </div>

      <div className="border border-border rounded-xl bg-card p-5 space-y-4">
        <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
          <Dumbbell className="w-4 h-4 text-foreground/85" /> Gym profile
        </h3>
        <div>
          <Label>Gym name</Label>
          <Input
            data-testid="gym-name-input"
            value={settings.gym_name || ""}
            onChange={(e) => setSettings({ ...settings, gym_name: e.target.value })}
            onBlur={() => save(settings)}
          />
        </div>
        <div>
          <Label>At-risk threshold (days)</Label>
          <Input
            data-testid="at-risk-days-input"
            type="number"
            value={settings.at_risk_days || 10}
            onChange={(e) => setSettings({ ...settings, at_risk_days: parseInt(e.target.value) || 10 })}
            onBlur={() => save(settings)}
          />
          <p className="text-xs text-muted-foreground mt-1">A member with no visit for this many days is flagged at-risk.</p>
        </div>
      </div>

      <div className="mt-6 border border-border rounded-xl bg-card p-5">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-foreground/85" /> Scheduled reminder cron
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Runs daily at 03:30 UTC. Queues renewal reminders (7d, 2d, 0d), trial reminders (day-before), and inactivity nudges based on your toggles above.
            </p>
            {cronInfo && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="cron-last-run">
                Last run: {new Date(cronInfo.ran_at).toLocaleString()} · {cronInfo.created} new reminder{cronInfo.created === 1 ? "" : "s"}
              </p>
            )}
          </div>
          <button
            onClick={runCron}
            disabled={cronRunning}
            data-testid="run-cron-btn"
            className="shrink-0 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-sm font-medium px-3.5 py-2 rounded-lg transition-colors active:scale-95"
          >
            <PlayCircle className="w-4 h-4" />
            {cronRunning ? "Running…" : "Run now"}
          </button>
        </div>
      </div>

      {/* Meta Lead Ads webhook */}
      <div className="mt-6 border border-border rounded-xl bg-card p-5" data-testid="webhook-section">
        <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2 mb-2">
          <Webhook className="w-4 h-4 text-foreground/85" /> Meta Lead Ads webhook
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Point your Meta Lead Ads integration (directly, or via Zapier / Make.com) at this URL. Each incoming lead is created automatically and sent a welcome WhatsApp template.
        </p>
        <div className="flex items-center gap-2 mb-3">
          <code
            data-testid="webhook-url"
            className="flex-1 text-xs font-mono bg-muted/50 border border-border rounded-lg px-3 py-2 text-foreground/85 truncate"
          >
            {gym?.webhook_url || "-"}
          </code>
          <button
            onClick={copyWebhook}
            data-testid="copy-webhook-btn"
            className="shrink-0 inline-flex items-center gap-1.5 bg-card border border-border hover:bg-muted/50 text-foreground/85 text-xs font-medium px-3 py-2 rounded-lg transition-colors"
          >
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          {isOwner && (
            <button
              onClick={rotateWebhook}
              disabled={rotating}
              data-testid="rotate-webhook-btn"
              className="shrink-0 inline-flex items-center gap-1.5 bg-card border border-border hover:bg-muted/50 text-foreground/85 text-xs font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              <RotateCw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} /> Rotate
            </button>
          )}
        </div>
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground select-none">Test payload (curl)</summary>
          <pre className="mt-2 bg-primary text-primary-foreground rounded-lg p-3 overflow-x-auto font-mono text-[11px]">
{`curl -X POST "${gym?.webhook_url || ""}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Test Lead","phone":"+919876500000","source":"Meta Ad"}'`}
          </pre>
        </details>
      </div>

      {/* Team */}
      <div className="mt-6 border border-border rounded-xl bg-card p-5" data-testid="team-section">
        <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-foreground/85" /> Team
        </h3>
        <ul className="divide-y divide-border/60 mb-4">
          {team.members.map(m => (
            <li key={m.user_id} className="flex items-center gap-3 py-2" data-testid={`team-member-${m.user_id}`}>
              {m.picture ? (
                <img src={m.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-muted text-xs font-medium text-muted-foreground flex items-center justify-center">
                  {m.name?.[0] || "U"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{m.name}</div>
                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
              </div>
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
                m.role === "owner" ? "bg-primary text-white" : "bg-muted text-foreground/85"
              }`}>
                {m.role}
              </span>
              {isOwner && m.role !== "owner" && m.user_id !== user.user_id && (
                <button
                  onClick={() => removeMember(m.user_id, m.name)}
                  data-testid={`remove-member-${m.user_id}`}
                  className="p-1.5 text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                  aria-label="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>

        {team.pending_invitations.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Pending invitations</p>
            <ul className="space-y-2">
              {team.pending_invitations.map(inv => (
                <li key={inv.id} className="flex items-center gap-3 text-sm">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" />
                  <span className="flex-1 truncate text-foreground/85">{inv.email}</span>
                  <span className="text-[10px] font-semibold uppercase text-muted-foreground/70">{inv.role}</span>
                  {isOwner && (
                    <button
                      onClick={() => revokeInvite(inv.id)}
                      data-testid={`revoke-invite-${inv.id}`}
                      className="p-1 text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 rounded-md"
                      aria-label="Revoke"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isOwner ? (
          <form onSubmit={invite} className="flex gap-2 pt-3 border-t border-border/60">
            <Input
              type="email"
              data-testid="invite-email-input"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="manager@yourgym.com"
              className="flex-1"
            />
            <button
              type="submit"
              data-testid="invite-btn"
              className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium px-3.5 py-2 rounded-lg transition-colors"
            >
              <Mail className="w-4 h-4" /> Invite
            </button>
          </form>
        ) : (
          <p className="text-xs text-muted-foreground/70 italic">Only owners can invite team members.</p>
        )}
      </div>

      <div className="mt-6 flex items-start gap-3 p-4 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-amber-900">
        <Zap className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">MVP note</div>
          <p className="text-xs mt-0.5 text-amber-800/80">
            Messages open via WhatsApp click-to-chat (wa.me). Scheduled reminders appear as actionable tasks on your Dashboard - tap Message and they're marked sent automatically.
          </p>
        </div>
      </div>

      {/* Automation builder - Coming Soon */}
      <div
        className="mt-8 relative overflow-hidden border border-dashed border-purple-300/60 dark:border-purple-700/40 rounded-2xl p-6 md:p-8"
        style={{
          background:
            "linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(219,39,119,0.04) 50%, rgba(249,115,22,0.04) 100%)",
        }}
        data-testid="automation-builder-coming-soon"
      >
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-purple-500/10 blur-3xl pointer-events-none" />
        <div className="flex items-start gap-4 relative">
          <span
            className="inline-flex items-center justify-center w-11 h-11 rounded-xl text-white shrink-0"
            style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 60%, #F97316 100%)" }}
            aria-hidden
          >
            <Zap className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-display text-base md:text-lg font-bold text-foreground">Automation builder</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider bg-foreground text-background px-1.5 py-0.5 rounded">
                Coming soon
              </span>
            </div>
            <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
              Drag-and-drop workflow builder - chain triggers (new lead, missed trial, expiring plan) with actions
              (send WhatsApp template, create task, assign to staff, wait N days). Build your own retention engine
              without writing a line of code.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["Visual flow editor", "Custom triggers", "Branching logic", "Multi-channel actions", "Goal tracking"].map(t => (
                <span key={t} className="text-[11px] font-medium px-2 py-1 rounded-full bg-card border border-border text-foreground/85">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
